import React, { useState } from 'react'
import { useTheme } from '@mui/material/styles'
import {
  Box,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  TableHead,
  Skeleton,
} from '@mui/material'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import LastPageIcon from '@mui/icons-material/LastPage'
import moment from 'moment'

export const PurchaseHistoryTable = (props) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const { historyData, htgCompany, showPoint = false } = props

  const arr =
    rowsPerPage > 0
      ? historyData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : historyData

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - historyData?.length) : 0

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 500 }} aria-label='custom pagination table'>
        <TableHead>
          <TableRow>
            <TableCell>Item Id</TableCell>
            {htgCompany !== 'htl' && <TableCell align='right'>Rim Size</TableCell>}
            <TableCell align='right'>Order Date</TableCell>
            <TableCell align='right'>Invoice Date</TableCell>
            <TableCell align='right'>Unit Price</TableCell>
            <TableCell align='right'>Quantity</TableCell>
            {htgCompany === 'htl' && <TableCell align='right'>Liter</TableCell>}
            <TableCell align='right'>Amount</TableCell>
            <TableCell align='right'>Discount</TableCell>
            <TableCell align='right'>Invoice ID</TableCell>
            {showPoint && <TableCell align='right'>Point</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {arr?.length
            ? arr.map((row, idx) => (
                <TableRow key={row.idx}>
                  <TableCell component='th' scope='row'>
                    {row.itemId}
                  </TableCell>
                  {htgCompany !== 'htl' && (
                    <TableCell style={{ width: 160 }} align='right'>
                      {row.rimSize}
                    </TableCell>
                  )}
                  <TableCell style={{ width: 160 }} align='right'>
                    {moment(row.orderDate).format('DD/MM/YY')}
                  </TableCell>
                  <TableCell style={{ width: 160 }} align='right'>
                    {moment(row.invoiceDate).format('DD/MM/YY')}
                  </TableCell>
                  <TableCell style={{ width: 160 }} align='right'>
                    {row.unitPrice}
                  </TableCell>
                  <TableCell style={{ width: 160 }} align='right'>
                    {htgCompany !== 'htl' ? row.orderQuantity : row?.htlQty}
                  </TableCell>
                  {htgCompany === 'htl' && (
                    <TableCell style={{ width: 160 }} align='right'>
                      {row.liter ?? '-'}
                    </TableCell>
                  )}
                  <TableCell style={{ width: 160 }} align='right'>
                    {row.amount}
                  </TableCell>
                  <TableCell style={{ width: 160 }} align='right'>
                    {row.discount}
                  </TableCell>
                  <TableCell style={{ width: 160 }} align='right'>
                    {row.invoiceId}
                  </TableCell>
                  {showPoint && (
                    <TableCell style={{ width: 160 }} align='right'>
                      {row.point ?? 0}
                    </TableCell>
                  )}
                </TableRow>
              ))
            : Array(10)
                .fill('')
                .map((x, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Skeleton style={{ width: '100%', marginBottom: 8 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton style={{ width: '100%', marginBottom: 8 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton style={{ width: '100%', marginBottom: 8 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton style={{ width: '100%', marginBottom: 8 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton style={{ width: '100%', marginBottom: 8 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton style={{ width: '100%', marginBottom: 8 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton style={{ width: '100%', marginBottom: 8 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton style={{ width: '100%', marginBottom: 8 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton style={{ width: '100%', marginBottom: 8 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton style={{ width: '100%', marginBottom: 8 }} />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                ))}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={10} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[10, 20, 30, { label: 'All', value: -1 }]}
              colSpan={10}
              count={historyData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: {
                  'aria-label': 'rows per page',
                },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  )
}

function TablePaginationActions(props) {
  const theme = useTheme()
  const { count, page, rowsPerPage, onPageChange } = props

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0)
  }

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1)
  }

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1)
  }

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label='first page'
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label='previous page'>
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='next page'
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='last page'
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  )
}
